import { useRef, useState, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Pen, Loader2, Sparkles } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiFetch } from "@/lib/api-fetch";

export function CharProgressBar({ charCount, limit }: { charCount: number; limit: number }) {
  if (!limit) return null;
  const pct = Math.min((charCount / limit) * 100, 100);
  const isOver = charCount > limit;
  const color = isOver
    ? "bg-red-500"
    : pct >= 90
    ? "bg-red-400"
    : pct >= 75
    ? "bg-amber-400"
    : "bg-emerald-500";
  return (
    <div className="mt-2 space-y-1">
      <div className="h-1 w-full rounded-full bg-secondary overflow-hidden">
        <motion.div
          className={`h-full rounded-full transition-colors ${color}`}
          animate={{ width: `${pct}%` }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
        />
      </div>
      <div className="flex justify-between text-[10px] font-mono text-muted-foreground">
        <span className={isOver ? "text-red-400 font-semibold" : pct >= 90 ? "text-amber-400" : ""}>
          {charCount.toLocaleString()} / {limit.toLocaleString()} chars
        </span>
        {isOver && (
          <span className="text-red-400 font-semibold">
            {(charCount - limit).toLocaleString()} over limit
          </span>
        )}
      </div>
    </div>
  );
}

interface RephraseTooltipProps {
  selection: string;
  position: { top: number; left: number };
  isLoading: boolean;
  onRephrase: () => void;
  onDismiss: () => void;
}

export function RephraseTooltip({ position, isLoading, onRephrase }: RephraseTooltipProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 4, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 4, scale: 0.95 }}
      transition={{ duration: 0.12 }}
      className="fixed z-50 pointer-events-auto"
      style={{ top: position.top, left: position.left, transform: "translateX(-50%)" }}
    >
      <button
        onMouseDown={(e) => { e.preventDefault(); if (!isLoading) onRephrase(); }}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold shadow-lg border border-primary/30 bg-card text-primary hover:bg-primary/10 transition-colors whitespace-nowrap disabled:opacity-60"
        disabled={isLoading}
      >
        {isLoading ? (
          <Loader2 className="w-3 h-3 animate-spin" />
        ) : (
          <Sparkles className="w-3 h-3" />
        )}
        {isLoading ? "Rephrasing…" : "Rephrase ✦"}
      </button>
    </motion.div>
  );
}

export interface ClickToEditPostProps {
  value: string;
  onChange: (v: string) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  placeholder?: string;
  platform?: string;
  sessionId: string;
  onUndoPush: (prev: string) => void;
  readOnly?: boolean;
}

export function ClickToEditPost({
  value,
  onChange,
  onKeyDown,
  placeholder,
  platform = "general",
  sessionId,
  onUndoPush,
  readOnly,
}: ClickToEditPostProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [selectionState, setSelectionState] = useState<{ text: string; position: { top: number; left: number } } | null>(null);
  const [isRephrasing, setIsRephrasing] = useState(false);
  const { toast } = useToast();

  const handleSelectionChange = useCallback(() => {
    if (!textareaRef.current || !isFocused) return;
    const start = textareaRef.current.selectionStart;
    const end = textareaRef.current.selectionEnd;
    const selected = textareaRef.current.value.slice(start, end).trim();
    if (selected.length < 2) {
      setSelectionState(null);
      return;
    }
    const textarea = textareaRef.current;
    const rect = textarea.getBoundingClientRect();
    const lineHeight = parseInt(getComputedStyle(textarea).lineHeight) || 24;
    const lines = textarea.value.slice(0, start).split("\n");
    const lineIndex = lines.length - 1;
    const approxTop = rect.top + lineIndex * lineHeight - 36;
    const approxLeft = rect.left + rect.width / 2;
    setSelectionState({ text: selected, position: { top: approxTop, left: approxLeft } });
  }, [isFocused]);

  useEffect(() => {
    document.addEventListener("selectionchange", handleSelectionChange);
    return () => document.removeEventListener("selectionchange", handleSelectionChange);
  }, [handleSelectionChange]);

  const handleRephrase = useCallback(async () => {
    if (!selectionState || isRephrasing) return;
    const { text: selected } = selectionState;
    setIsRephrasing(true);
    setSelectionState(null);
    try {
      const baseUrl = import.meta.env.BASE_URL.replace(/\/$/, "");
      const resp = await apiFetch(`${baseUrl}/api/posts/rephrase`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-session-id": sessionId },
        body: JSON.stringify({ selection: selected, fullPost: value, platform }),
      });
      if (resp.ok) {
        const data = await resp.json() as { rephrased: string };
        onUndoPush(value);
        const newContent = value.replace(selected, data.rephrased);
        onChange(newContent);
      } else {
        toast({ title: "Rephrase failed", description: "Could not rephrase selection.", variant: "destructive" });
      }
    } catch {
      toast({ title: "Rephrase failed", description: "Could not rephrase selection.", variant: "destructive" });
    } finally {
      setIsRephrasing(false);
    }
  }, [selectionState, isRephrasing, value, platform, sessionId, onChange, onUndoPush, toast]);

  const handleMouseUp = () => {
    setTimeout(handleSelectionChange, 10);
  };

  return (
    <div ref={containerRef} className="relative w-full group">
      <AnimatePresence>
        {selectionState && !isRephrasing && (
          <RephraseTooltip
            key="rephrase-tooltip"
            selection={selectionState.text}
            position={selectionState.position}
            isLoading={isRephrasing}
            onRephrase={handleRephrase}
            onDismiss={() => setSelectionState(null)}
          />
        )}
        {isRephrasing && (
          <motion.div
            key="rephrasing-indicator"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 z-10 flex items-center justify-center bg-background/60 rounded-xl backdrop-blur-sm pointer-events-none"
          >
            <div className="flex items-center gap-2 text-primary text-sm font-medium">
              <Loader2 className="w-4 h-4 animate-spin" /> Rephrasing…
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {!readOnly && !isFocused && value && (
        <div className="absolute top-2 right-2 z-10 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
          <Pen className="w-3 h-3 text-primary/60" />
          <span className="text-[10px] text-primary/60 font-medium">Click to edit</span>
        </div>
      )}

      <textarea
        ref={textareaRef}
        data-testid="post-output-textarea"
        className={`w-full min-h-[200px] bg-transparent text-lg leading-relaxed text-foreground resize-y outline-none rounded-xl p-4 transition-all duration-200 placeholder:text-muted-foreground/40 ${
          isFocused
            ? "border border-primary/40 ring-1 ring-primary/20"
            : value
            ? "border border-border/50 hover:border-primary/30 hover:bg-primary/[0.015]"
            : "border border-border/50"
        }`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => { setIsFocused(false); setSelectionState(null); }}
        onKeyDown={onKeyDown}
        onMouseUp={handleMouseUp}
        onKeyUp={handleSelectionChange}
        placeholder={placeholder}
        readOnly={readOnly}
        style={{ cursor: readOnly ? "default" : isFocused ? "text" : value ? "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='%236366f1' stroke-width='2'%3E%3Cpath d='M17 3a2.83 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z'/%3E%3C/svg%3E\") 0 20, text" : "text" }}
      />
    </div>
  );
}
